import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { LoadingDots } from '../components/LoadingDots';

interface VerifyCert {
  standard: string;
  tier: number;
  tierName: string;
  type: 'test' | 'production';
  status: string;
  issuedAt: string;
  expiresAt: string;
  devices: number;
  verifiedAt: string;
}

interface VerifyResponse {
  organization: string;
  certifications: VerifyCert[];
  verified: boolean;
  verificationUrl: string;
}

/** Route: `/verify/:orgId`. Public certification verification page. */
export function VerifyPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [data, setData] = useState<VerifyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    apiFetch<VerifyResponse>(`/v1/certs/verify/${orgId}`, { skipAuth: true })
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'verification failed'));
  }, [orgId]);

  if (error) {
    return (
      <div>
        <div className="text-muted">── AstdZ™ certification verification ─────────────────────</div>
        <p className="text-danger" style={{ marginTop: 12 }}>✗ {error}</p>
      </div>
    );
  }
  if (!data) return <LoadingDots />;

  const nowIso = new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 12 }}>── AstdZ™ certification verification ─────────────────────</div>

      <div style={{ marginBottom: 8 }}>
        <span className="text-muted">organization    </span>{data.organization}
      </div>
      <div style={{ marginBottom: 16 }}>
        <span className="text-muted">status          </span>
        {data.verified
          ? <span className="text-success">✓ verified</span>
          : <span className="text-danger">✗ no active certifications</span>}
      </div>

      <div className="text-muted" style={{ marginBottom: 12 }}>── active certifications ─────────────────────────────────</div>

      {data.certifications.length === 0 && (
        <p className="text-muted">no active certifications on record.</p>
      )}

      {data.certifications.map((c, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>
            <strong>{c.standard}</strong> · Tier {c.tier} {c.tierName} · {c.type.toUpperCase()}
          </div>
          <div><span className="text-muted">issued    </span>{c.issuedAt}</div>
          <div><span className="text-muted">expires   </span>{c.expiresAt}</div>
          <div><span className="text-muted">devices   </span>{c.devices} registered</div>
        </div>
      ))}

      <div className="text-muted" style={{ marginTop: 12 }}>─────────────────────────────────────────────────────────</div>
      <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 8 }}>
        verified by AstdZ™ · astdz.com
      </p>
      <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
        last checked: {nowIso}
      </p>
    </div>
  );
}
